import React from 'react';
import {StyleSheet, View} from 'react-native';

import {getSkeletonColors, STAGGER} from '../constants';
import {Bone} from '../primitives/Bone';
import {BoneText} from '../primitives/BoneText';

/**
 * Skeleton for DiscoverTile in the masonry grid.
 * Mirrors: BaseTile shell + DiscoverTile's headline-only customStyles
 *   (frontend/app/components/articleTile/DiscoverTile.tsx,
 *    frontend/app/components/articleTile/BaseTile.tsx:123-150).
 *
 * The `imageHeight` prop varies the image height so the masonry layout looks
 * organic during load instead of a uniform grid of equal-height cards.
 */
type Props = {
  /** Image bone height in pixels. Vary across the grid for masonry feel. */
  imageHeight?: number;
  /** Stagger offset so adjacent tiles pulse in waves rather than in unison. */
  index?: number;
};

export const DiscoverTileSkeleton: React.FC<Props> = React.memo(
  ({imageHeight = 140, index = 0}) => {
    const base = (index % 4) * STAGGER.interval;
    return (
      <View style={styles.container} accessibilityElementsHidden>
        <Bone width="100%" height={imageHeight} borderRadius={0} animationDelay={base} />
        <View style={styles.textBlock}>
          <BoneText variant="h1" width="90%" animationDelay={base + STAGGER.delay(1)} />
          <BoneText variant="h1" width="55%" animationDelay={base + STAGGER.delay(2)} />
        </View>
      </View>
    );
  },
);

DiscoverTileSkeleton.displayName = 'DiscoverTileSkeleton';

const styles = StyleSheet.create({
  container: {
    borderRadius: 4,
    borderWidth: 0.5,
    borderColor: getSkeletonColors().border,
    backgroundColor: getSkeletonColors().cardBackground,
    overflow: 'hidden',
    marginBottom: 8,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 5},
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    elevation: 7,
  },
  textBlock: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 6,
  },
});
